/**
 * Peças de apresentação do Painel de Assistências v2 (escopo .fin2):
 * Card com régua, KPI clicável (drill), Pareto com % acumulado, tabela
 * compacta de layout fixo (zero scroll lateral) e percentis.
 *
 * "Rótulo tem que dizer a régua" (ADR-0059): todo card recebe `regua` e a
 * mostra no subtítulo — o gestor sabe o que o número mede e o que não mede.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiInfo } from "react-icons/fi";

import { VazioInline } from "../_shared/Estados";
import { GLOSSARIO, textoDeAjuda } from "./ajuda";
import { inteiro } from "./recorte";

// Cor só comunica estado.
export const TONE_COR = {
  info: "var(--chart-2)",
  warning: "var(--warning-solid)",
  danger: "var(--danger-solid)",
  success: "var(--success-solid)",
  neutral: "var(--ink-4)",
};

// Tooltip dos gráficos (recharts) seguindo o tema: o padrão da lib é caixa
// branca fixa, ilegível no modo escuro.
export const TOOLTIP_TEMA = {
  contentStyle: {
    background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
    color: "var(--ink)", fontSize: 12.5, boxShadow: "var(--shadow-2)",
  },
  labelStyle: { color: "var(--ink-2)", fontWeight: 600, marginBottom: 2 },
  itemStyle: { color: "var(--ink)" },
  cursor: { fill: "var(--surface-2)", stroke: "var(--border)", fillOpacity: 0.5 },
};

const LARGURA_AJUDA = 280;

/**
 * Ícone discreto de ajuda: ao clicar, abre uma explicação curta do que o
 * número/gráfico mostra. O balão vai por portal com posição fixa para nunca
 * ser cortado pelo card; fecha com Esc, clique fora, rolagem ou novo clique.
 */
export function Ajuda({ titulo, texto, style }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const balaoRef = useRef(null);

  useEffect(() => {
    if (!pos) return undefined;
    const fechar = () => setPos(null);
    const aoClicar = (e) => {
      if (btnRef.current?.contains(e.target) || balaoRef.current?.contains(e.target)) return;
      fechar();
    };
    const aoTeclar = (e) => e.key === "Escape" && fechar();
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    window.addEventListener("scroll", fechar, true);
    window.addEventListener("resize", fechar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
      window.removeEventListener("scroll", fechar, true);
      window.removeEventListener("resize", fechar);
    };
  }, [pos]);

  if (!texto) return null;

  const alternar = (e) => {
    e.stopPropagation();
    if (pos) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.right - LARGURA_AJUDA, window.innerWidth - LARGURA_AJUDA - 8));
    setPos({ top: r.bottom + 6, left });
  };

  return (
    <>
      <button
        ref={btnRef} type="button" onClick={alternar}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={`O que significa: ${titulo}`} aria-expanded={!!pos} title="O que é isto?"
        style={{
          flex: "none", display: "grid", placeItems: "center", width: 22, height: 22, padding: 0,
          border: 0, borderRadius: "50%", background: "transparent", cursor: "pointer",
          color: "var(--ink-3)", opacity: pos ? 1 : 0.55, ...style,
        }}
      >
        <FiInfo size={13} aria-hidden="true" />
      </button>
      {pos && createPortal(
        <div ref={balaoRef} role="note" onClick={(e) => e.stopPropagation()}
             style={{
               position: "fixed", top: pos.top, left: pos.left, width: LARGURA_AJUDA, zIndex: 1200,
               padding: "12px 14px", borderRadius: 10, background: "var(--surface)",
               border: "1px solid var(--border)", boxShadow: "var(--shadow-2)",
               color: "var(--ink)", fontSize: 12.5, lineHeight: 1.5, textAlign: "left",
               textTransform: "none", letterSpacing: "normal", fontWeight: 400,
             }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{titulo}</div>
          <div style={{ color: "var(--ink-2)" }}>{texto}</div>
          {/p50|d\.u\.|ac\.|Pareto/i.test(texto) && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)", color: "var(--ink-3)", fontSize: 11.5 }}>
              {GLOSSARIO}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

export function Card({ titulo, regua, span = 4, acao, ajuda, children }) {
  return (
    <div className={`cc span-${span}`}>
      <div className="cc__head">
        <div style={{ minWidth: 0 }}>
          <div className="cc__title">{titulo}</div>
          {regua && <div className="cc__sub" title={regua}>{regua}</div>}
        </div>
        <div className="cc__actions" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {acao}
          <Ajuda titulo={titulo} texto={ajuda ?? textoDeAjuda(titulo)} />
        </div>
      </div>
      <div className="cc__body">{children}</div>
    </div>
  );
}

export function KpiStrip({ children }) {
  return (
    <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14, flexWrap: "wrap" }}>
      {children}
    </div>
  );
}

/** KPI; com `onClick` vira botão que abre a lista (drill). */
export function Kpi({ rotulo, valor, meta, tone, onClick, titulo, ajuda }) {
  const clicavel = typeof onClick === "function";
  const cor = tone && tone !== "neutral" ? TONE_COR[tone] : undefined;
  return (
    <div
      className="kpi-v2"
      role={clicavel ? "button" : undefined}
      tabIndex={clicavel ? 0 : undefined}
      title={titulo || (clicavel ? "Ver a lista" : undefined)}
      style={{ position: "relative", ...(clicavel ? { cursor: "pointer" } : {}) }}
      onClick={onClick}
      onKeyDown={clicavel ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    >
      <Ajuda titulo={rotulo} texto={ajuda ?? textoDeAjuda(rotulo)}
             style={{ position: "absolute", top: 8, right: 8 }} />
      <div className="lbl" style={{ paddingRight: 22 }}>{rotulo}</div>
      <div className="val" style={cor ? { color: cor } : undefined}>{valor}</div>
      {meta && <div className="meta">{meta}{clicavel ? " · ver lista" : ""}</div>}
    </div>
  );
}

/**
 * Pareto horizontal: barra = total, texto = total · % · % acumulado.
 * `linhas`: [{codigo, label, total, pct, pct_acumulado}] (já ordenadas).
 */
export function Pareto({ linhas, vazio = "Sem dados no recorte.", cor = "var(--chart-2)", formatar = inteiro, onSelecionar }) {
  const dados = linhas || [];
  if (dados.length === 0) return <VazioInline>{vazio}</VazioInline>;
  const max = Math.max(1, ...dados.map((d) => Number(d.total) || 0));
  return (
    <div className="stack" style={{ gap: 9 }}>
      {dados.map((d) => {
        const clicavel = onSelecionar && d.codigo !== "__outros__" && d.codigo !== "—";
        return (
          <div key={d.codigo}
               role={clicavel ? "button" : undefined} tabIndex={clicavel ? 0 : undefined}
               style={clicavel ? { cursor: "pointer" } : undefined}
               title={clicavel ? "Filtrar o painel por este valor" : undefined}
               onClick={clicavel ? () => onSelecionar(d) : undefined}
               onKeyDown={clicavel ? (e) => e.key === "Enter" && onSelecionar(d) : undefined}>
            <div className="flex between" style={{ fontSize: 12.5, marginBottom: 3, gap: 8 }}>
              <span className="ell" style={{ minWidth: 0 }}>{d.label || "—"}</span>
              <span className="mono" style={{ flexShrink: 0 }}>
                <strong>{formatar(d.total)}</strong>
                {d.pct !== undefined && <span className="muted"> · {d.pct}%</span>}
                {d.pct_acumulado !== undefined && <span className="muted tiny"> (ac. {d.pct_acumulado}%)</span>}
              </span>
            </div>
            <div style={{ height: 7, borderRadius: 4, background: "var(--surface-2)", overflow: "hidden" }}>
              <div style={{ width: `${((Number(d.total) || 0) / max) * 100}%`, height: "100%", borderRadius: 4, background: cor }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Barras simples com tom por linha ({label, total, tone}). */
export function BarrasTom({ linhas, vazio = "Sem dados.", onSelecionar }) {
  const dados = (linhas || []);
  if (dados.every((d) => !Number(d.total))) return <VazioInline>{vazio}</VazioInline>;
  const max = Math.max(1, ...dados.map((d) => Number(d.total) || 0));
  return (
    <div className="stack" style={{ gap: 10 }}>
      {dados.map((d) => (
        <div key={d.label}
             role={onSelecionar ? "button" : undefined} tabIndex={onSelecionar ? 0 : undefined}
             style={onSelecionar ? { cursor: "pointer" } : undefined}
             onClick={onSelecionar ? () => onSelecionar(d) : undefined}
             onKeyDown={onSelecionar ? (e) => e.key === "Enter" && onSelecionar(d) : undefined}>
          <div className="flex between" style={{ fontSize: 12.5, marginBottom: 3 }}>
            <span className="ell">{d.label}</span>
            <strong className="mono">{inteiro(d.total)}</strong>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: "var(--surface-2)", overflow: "hidden" }}>
            <div style={{ width: `${((Number(d.total) || 0) / max) * 100}%`, height: "100%", borderRadius: 4, background: TONE_COR[d.tone] || "var(--chart-2)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Tabela compacta. `colunas`: [{chave, rotulo, largura, num, render}].
 * table-layout fixed + ellipsis com tooltip: nunca rola para o lado.
 */
export function TabelaSimples({ colunas, linhas, vazio = "Sem dados no recorte.", onLinha }) {
  if (!linhas || linhas.length === 0) return <VazioInline>{vazio}</VazioInline>;
  return (
    <div style={{ margin: "0 -18px -18px", overflowX: "hidden" }}>
      <table className="dtable" style={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          {colunas.map((c) => <col key={c.chave} style={c.largura ? { width: c.largura } : undefined} />)}
        </colgroup>
        <thead>
          <tr>{colunas.map((c) => <th key={c.chave} style={c.num ? { textAlign: "right" } : undefined} title={c.dica}>{c.rotulo}</th>)}</tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={l.id || l.codigo || i} className={onLinha ? "rowlink" : undefined}
                tabIndex={onLinha ? 0 : undefined}
                onClick={onLinha ? () => onLinha(l) : undefined}
                onKeyDown={onLinha ? (e) => e.key === "Enter" && onLinha(l) : undefined}>
              {colunas.map((c) => {
                const v = c.render ? c.render(l) : l[c.chave];
                const texto = typeof v === "string" || typeof v === "number" ? String(v) : undefined;
                return (
                  <td key={c.chave} className={c.num ? "num" : "ell"} title={texto}
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {v ?? "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** p50 · p80 · p95 (n) — `formato` formata cada valor. */
export function Percentis({ p, formato = (v) => v }) {
  if (!p || !p.n) return <span className="muted">sem casos</span>;
  return (
    <span className="mono">
      {formato(p.p50)} <span className="muted tiny">p50</span> · {formato(p.p80)} <span className="muted tiny">p80</span> · {formato(p.p95)} <span className="muted tiny">p95</span>
      <span className="muted tiny"> · n={p.n}</span>
    </span>
  );
}

/** Aviso de régua em destaque (ex.: custo estimado fora do Financeiro). */
export function Regua({ children, tone = "info" }) {
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 10, fontSize: 12.5, marginBottom: 14,
      background: `var(--${tone}-bg)`, color: `var(--${tone}-text)`, border: `1px solid var(--${tone}-border)`,
    }}>
      {children}
    </div>
  );
}
